import type { ApprovalRunOrigin, BulkRejectResultRow } from '../models/insightAction.types'
import { adaptBulkLookup, adaptRejectResults } from '../service/adaptRun'
import { classifyInsightActionError } from '../service/classifyInsightActionError'

import type { InsightActionApiSlice, RunScope } from './insightActionFlow.types'

export type RunDecisionResult<T> = { ok: true; value: T } | { ok: false; code: string }

export interface ApprovalRunFlowDeps {
  api: InsightActionApiSlice<'bulkLookupOrigin' | 'approveRun' | 'rejectRun'>
}

/**
 * The approvals queue's three calls into a run (F5, ORP-22267): one origin lookup for the whole
 * page, and the run-level approve / reject. Each item is still recorded as its own decision
 * server-side — the group is a view, not a single decision.
 */
export class ApprovalRunFlow {
  constructor(private readonly deps: ApprovalRunFlowDeps) {}

  /**
   * One call answers the whole page, however many runs it spans. A failed lookup is not an error
   * to show: every request simply stays its own row, and the queue still works.
   */
  async lookup(
    scope: RunScope,
    requestIds: readonly string[],
    signal?: AbortSignal,
  ): Promise<Record<string, ApprovalRunOrigin | null>> {
    if (requestIds.length === 0) return {}
    try {
      const dto = await this.deps.api.bulkLookupOrigin(
        scope.accountId,
        scope.workspaceId,
        [...requestIds],
        signal,
      )
      return adaptBulkLookup(dto)
    } catch {
      return {}
    }
  }

  /** An empty `itemIds` means every pending item of the run; the server reads it that way too. */
  async approve(
    scope: RunScope,
    runId: string,
    itemIds: readonly number[],
  ): Promise<RunDecisionResult<number>> {
    try {
      const response = await this.deps.api.approveRun(
        scope.accountId,
        scope.workspaceId,
        runId,
        body(itemIds),
      )
      return { ok: true, value: response.items_queued ?? 0 }
    } catch (cause) {
      return { ok: false, code: classifyInsightActionError(cause).code }
    }
  }

  /** Reject settles synchronously, so its per-item results are final. */
  async reject(
    scope: RunScope,
    runId: string,
    itemIds: readonly number[],
  ): Promise<RunDecisionResult<BulkRejectResultRow[]>> {
    try {
      const response = await this.deps.api.rejectRun(
        scope.accountId,
        scope.workspaceId,
        runId,
        body(itemIds),
      )
      return { ok: true, value: adaptRejectResults(response) }
    } catch (cause) {
      return { ok: false, code: classifyInsightActionError(cause).code }
    }
  }
}

function body(itemIds: readonly number[]): { item_ids?: number[] } {
  return itemIds.length ? { item_ids: [...itemIds] } : {}
}
